import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileUp } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, Field, Section, Select, Switch } from '@zt/ui';
import { api, ApiError, newIdempotencyKey } from '../lib/api';
import { saveText, toCsv } from '../lib/csv';
import { HelpTip, PageFrame } from '../lib/help';
import { useT } from '../lib/i18n';
import { useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  CategoryImportPreview,
  CategoryImportResult,
  ImportedMediaStatus,
  DealerImportPreview,
  DealerImportResult,
  ProductImportPreview,
  ProductImportResult,
  ProductImportStatus,
  Warehouse,
} from '../lib/types';
import { useGroups, usePriceLists } from './pricing';
import { useCatalogRefs } from './products';

/** Dosyayı tarayıcıda indirir (oturum çereziyle; bağlantı paylaşılamaz). */
async function download(path: string, fallbackName: string) {
  const res = await fetch(path, { credentials: 'include' });
  if (!res.ok) throw new Error(res.statusText);
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(cd)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export function FilePicker({
  onFile,
  busy,
  label,
  accept = '.csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}: {
  onFile: (f: File) => void;
  busy: boolean;
  label: string;
  accept?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        accept={accept}
        aria-label={label}
        tabIndex={-1}
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          if (input.current) input.current.value = '';
        }}
      />
      <Button variant="primary" loading={busy} onClick={() => input.current?.click()}>
        <FileUp className="h-4 w-4" aria-hidden />
        {label}
      </Button>
    </>
  );
}

export function DownloadButton({ path, name, label }: { path: string; name: string; label: string }) {
  return (
    <Button
      size="sm"
      onClick={() =>
        download(path, name).catch((e: unknown) => toast.error(e instanceof Error ? e.message : String(e)))
      }
    >
      <Download className="h-4 w-4" aria-hidden />
      {label}
    </Button>
  );
}

function Issues({
  issues,
  fileName,
}: {
  issues: Array<{ row: number; field?: string; code: string; message: string }>;
  /** İndirilen hata listesinin adı için (ör. "urunler"). */
  fileName: string;
}) {
  const t = useT();
  if (!issues.length) return null;
  const problem = (i: (typeof issues)[number]) =>
    t(`errors.${i.code}`, { column: i.message, row: i.message });
  const detail = (i: (typeof issues)[number]) =>
    i.message && !['column_missing', 'duplicate_in_file'].includes(i.code) ? i.message : '';
  const saveIssues = () =>
    saveText(
      `${fileName}-hatalar.csv`,
      toCsv(
        [
          t('admin.imports.issueRow'),
          t('admin.imports.issueField'),
          t('admin.imports.issueProblem'),
          t('admin.imports.issueValue'),
        ],
        issues.map((i) => [i.row, i.field ?? '', problem(i), detail(i)]),
      ),
    );
  return (
    <div className="rounded-sm border border-amber/30 bg-amber-tint p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-amber">
          {t('admin.imports.issues', { count: issues.length })}
        </p>
        <span className="flex items-center gap-1">
          <Button size="sm" onClick={saveIssues}>
            <Download className="h-4 w-4" aria-hidden />
            {t('admin.imports.downloadIssues')}
          </Button>
          <HelpTip k="imports.download_issues" />
        </span>
      </div>
      <ul className="max-h-72 space-y-0.5 overflow-auto text-sm">
        {issues.map((i, k) => (
          <li key={k}>
            {t('admin.imports.row', { row: i.row })}
            {i.field ? ` · ${i.field}` : ''}: {problem(i)}
            {detail(i) ? ` (${detail(i)})` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ImportsPage() {
  const { can } = usePermissions();
  return (
    <PageFrame helpKey="page.imports">
      {can('catalog.import') && <CategoryImport />}
      {can('catalog.import') && <ProductImport />}
      {can('b2b.dealer.manage') && <DealerImport />}
    </PageFrame>
  );
}

/** Kategori listesi içe/dışa aktarma (F3B-13): önizleme → uygula, tek işlemde. */
function CategoryImport() {
  const t = useT();
  const { can } = usePermissions();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<CategoryImportPreview | null>(null);
  const [result, setResult] = useState<CategoryImportResult | null>(null);
  const apply = useApiMutation(
    (id: string) =>
      api.post<CategoryImportResult>(`/v1/admin/imports/categories/${id}/apply`, undefined, {
        idempotencyKey: newIdempotencyKey(),
      }),
    {
      invalidate: [['categories']],
      onSuccess: (r) => {
        setResult(r);
        setPreview(null);
        toast.success(t('admin.imports.applied'));
      },
    },
  );
  const upload = async (file: File) => {
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      setPreview(await api.upload<CategoryImportPreview>('/v1/admin/imports/categories/preview', fd));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.title : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section
      title={t('admin.imports.categories')}
      description={t('admin.imports.categoriesLead')}
      action={
        <div className="flex flex-wrap gap-2">
          <DownloadButton
            path="/v1/admin/imports/categories/template?format=xlsx"
            name="kategori-sablonu.xlsx"
            label={t('admin.imports.template')}
          />
          {can('catalog.product.read') && (
            <DownloadButton
              path="/v1/admin/exports/categories?format=xlsx"
              name="kategoriler.xlsx"
              label={t('admin.imports.exportCategories')}
            />
          )}
          <HelpTip k="imports.categories_export" />
        </div>
      }
    >
      <div className="flex items-center gap-1">
        <FilePicker busy={busy} onFile={upload} label={t('admin.imports.chooseFile')} />
        <HelpTip k="imports.categories_file" />
      </div>
      {preview && (
        <div className="mt-4 space-y-3 rounded-md border border-line p-4">
          <div className="flex flex-wrap gap-2">
            <Badge tone="moss">{t('admin.imports.toCreate', { count: preview.summary.create })}</Badge>
            <Badge tone="pine">{t('admin.imports.toUpdate', { count: preview.summary.update })}</Badge>
            <Badge tone="neutral">{t('admin.imports.unchanged', { count: preview.summary.unchanged })}</Badge>
            {preview.summary.errorRows > 0 && (
              <Badge tone="amber">{t('admin.imports.errorRows', { count: preview.summary.errorRows })}</Badge>
            )}
          </div>
          {preview.unknownHeaders.length > 0 && (
            <p className="text-sm text-ink-3">
              {t('admin.imports.unknownHeaders', { list: preview.unknownHeaders.join(', ') })}
            </p>
          )}
          <Issues issues={preview.issues} fileName="kategori-ice-aktarma" />
          {preview.rows.length > 0 && (
            <div className="max-h-72 overflow-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">{t('admin.imports.categoriesPreview')}</caption>
                <thead className="text-ink-2">
                  <tr>
                    <th className="py-1 text-start font-medium">{t('admin.imports.issueRow')}</th>
                    <th className="py-1 text-start font-medium">{t('admin.categories.name')}</th>
                    <th className="py-1 text-start font-medium">{t('admin.categories.parent')}</th>
                    <th className="py-1 text-start font-medium">{t('admin.categories.slug')}</th>
                    <th className="py-1 text-start font-medium">{t('admin.imports.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row} className="border-t border-line">
                      <td className="py-1 tabular">{r.row}</td>
                      <td className="py-1">{r.name ?? '—'}</td>
                      <td className="py-1">{r.parent ?? '—'}</td>
                      <td className="py-1 tabular">{r.key || '—'}</td>
                      <td className="py-1">{t(`admin.imports.actions.${r.action}`)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <span className="flex items-center gap-1">
            <Button
              variant="primary"
              loading={apply.isPending}
              disabled={
                preview.summary.create + preview.summary.update === 0 || !can('catalog.product.write')
              }
              onClick={() => apply.mutate(preview.jobId)}
            >
              {t('admin.imports.apply')}
            </Button>
            <HelpTip k="imports.categories_apply" />
          </span>
        </div>
      )}
      {result && (
        <p className="mt-4 rounded-md border border-line p-4 text-sm font-medium" role="status">
          {t('admin.imports.categoryResult', {
            created: result.summary.created,
            updated: result.summary.updated,
          })}
        </p>
      )}
    </Section>
  );
}

function ProductImport() {
  const t = useT();
  const { can } = usePermissions();
  const lists = usePriceLists();
  const { brands } = useCatalogRefs();
  const warehouses = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => api.get<Warehouse[]>('/v1/admin/warehouses'),
    enabled: can('inventory.stock.read'),
  });
  const [opts, setOpts] = useState({
    priceListId: '',
    warehouseId: '',
    defaultBrandId: '',
    healthContent: true,
    createMissingCategories: false,
    categoryMode: 'merge' as 'merge' | 'replace',
  });
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<ProductImportPreview | null>(null);
  /** Uygulanan iş: sonuç ve (büyük dosyada) ilerleme bu işten okunur (F5B-10). */
  const [jobId, setJobId] = useState<string | null>(null);
  /** Son seçilen dosya: "Önerilen SKU'larla" veya "eşleştirmeyle yeniden önizle" aynı dosyayı yeniden gönderir. */
  const [lastFile, setLastFile] = useState<File | null>(null);
  /** Elle sütun eşleştirme: dosya başlığı → alan veya "ignore". */
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});
  const job = useQuery({
    queryKey: ['product-import-job', jobId],
    queryFn: () => api.get<ProductImportStatus>(`/v1/admin/imports/products/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (q) =>
      q.state.data && q.state.data.status !== 'queued' && q.state.data.status !== 'running' ? false : 2000,
  });
  const finished = job.data?.status === 'applied';
  useEffect(() => {
    if (finished) {
      void qc.invalidateQueries({ queryKey: ['products'] });
      void qc.invalidateQueries({ queryKey: ['imported-media-status'] });
    }
  }, [finished, qc]);
  const apply = useApiMutation(
    (id: string) =>
      api.post<ProductImportResult>(`/v1/admin/imports/products/${id}/apply`, undefined, {
        idempotencyKey: newIdempotencyKey(),
      }),
    {
      onSuccess: (r) => {
        setJobId(r.jobId);
        void qc.invalidateQueries({ queryKey: ['product-import-job', r.jobId] });
        setPreview(null);
        toast.success(r.queued ? t('admin.imports.importQueuedLead') : t('admin.imports.applied'));
      },
    },
  );
  const retry = useApiMutation(
    (id: string) =>
      api.post<ProductImportResult>(`/v1/admin/imports/products/${id}/retry`, undefined, {
        idempotencyKey: newIdempotencyKey(),
      }),
    {
      onSuccess: (r) => void qc.invalidateQueries({ queryKey: ['product-import-job', r.jobId] }),
    },
  );
  // Art arda seçilen dosyalarda yalnızca son önizlemenin yanıtı kullanılır (eski yanıt geç gelirse yok sayılır).
  const uploadSeq = useRef(0);
  const upload = async (file: File, generateMissingSkus = false, map: Record<string, string> = {}) => {
    const mine = ++uploadSeq.current;
    setBusy(true);
    setJobId(null);
    setLastFile(file);
    if (file !== lastFile) setColumnMap(map);
    try {
      const qs = new URLSearchParams();
      if (Object.keys(map).length) qs.set('columnMap', JSON.stringify(map));
      if (opts.priceListId) qs.set('priceListId', opts.priceListId);
      if (opts.warehouseId) qs.set('warehouseId', opts.warehouseId);
      if (opts.defaultBrandId) qs.set('defaultBrandId', opts.defaultBrandId);
      qs.set('healthContent', String(opts.healthContent));
      if (generateMissingSkus) qs.set('generateMissingSkus', 'true');
      if (opts.createMissingCategories) qs.set('createMissingCategories', 'true');
      qs.set('categoryMode', opts.categoryMode);
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.upload<ProductImportPreview>(
        `/v1/admin/imports/products/preview?${qs.toString()}`,
        fd,
      );
      if (mine === uploadSeq.current) setPreview(r);
    } catch (e) {
      if (mine === uploadSeq.current) toast.error(e instanceof ApiError ? e.title : String(e));
    } finally {
      if (mine === uploadSeq.current) setBusy(false);
    }
  };
  return (
    <Section
      title={t('admin.imports.products')}
      description={t('admin.imports.productsLead')}
      action={
        <div className="flex flex-wrap gap-2">
          <DownloadButton
            path="/v1/admin/imports/products/template?format=xlsx"
            name="urun-sablonu.xlsx"
            label={t('admin.imports.template')}
          />
          {can('catalog.product.read') && (
            <>
              <DownloadButton
                path={`/v1/admin/exports/products?format=xlsx${opts.priceListId ? `&priceListId=${opts.priceListId}` : ''}`}
                name="urunler.xlsx"
                label={t('admin.imports.exportProducts')}
              />
              <DownloadButton
                path={`/v1/admin/exports/products?format=json${opts.priceListId ? `&priceListId=${opts.priceListId}` : ''}`}
                name="urunler.json"
                label={t('admin.imports.exportJson')}
              />
              <DownloadButton
                path={`/v1/admin/exports/products?format=xml${opts.priceListId ? `&priceListId=${opts.priceListId}` : ''}`}
                name="urunler.xml"
                label={t('admin.imports.exportXml')}
              />
            </>
          )}
          <HelpTip k="imports.export" />
        </div>
      }
    >
      <div className="grid gap-3 sm:grid-cols-3">
        {can('pricing.price.write') && (
          <Field label={t('admin.imports.priceList')} help={<HelpTip k="imports.price_list" />}>
            {(p) => (
              <Select
                {...p}
                value={opts.priceListId}
                onChange={(e) => setOpts({ ...opts, priceListId: e.target.value })}
              >
                <option value="">{t('admin.imports.skipPrices')}</option>
                {lists.data?.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        {can('inventory.stock.write') && (
          <Field label={t('admin.imports.warehouse')} help={<HelpTip k="imports.warehouse" />}>
            {(p) => (
              <Select
                {...p}
                value={opts.warehouseId}
                onChange={(e) => setOpts({ ...opts, warehouseId: e.target.value })}
              >
                <option value="">{t('admin.imports.skipStock')}</option>
                {warehouses.data?.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label={t('admin.imports.defaultBrand')} help={<HelpTip k="imports.default_brand" />}>
          {(p) => (
            <Select
              {...p}
              value={opts.defaultBrandId}
              onChange={(e) => setOpts({ ...opts, defaultBrandId: e.target.value })}
            >
              <option value="">—</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <Switch
          checked={opts.healthContent}
          onCheckedChange={(v) => setOpts({ ...opts, healthContent: v })}
          label={t('admin.imports.healthContent')}
        />
        <span className="text-sm">{t('admin.imports.healthContent')}</span>
        <HelpTip k="products.health_content" />
      </div>
      <div className="mt-3 flex items-center gap-3">
        <Switch
          checked={opts.createMissingCategories}
          onCheckedChange={(v) => setOpts({ ...opts, createMissingCategories: v })}
          label={t('admin.imports.createMissingCategories')}
        />
        <span className="text-sm">{t('admin.imports.createMissingCategories')}</span>
        <HelpTip k="imports.create_missing_categories" />
      </div>
      <div className="mt-3 max-w-md">
        <Field label={t('admin.imports.categoryMode')} help={<HelpTip k="imports.category_mode" />}>
          {(p) => (
            <Select
              {...p}
              value={opts.categoryMode}
              onChange={(e) => setOpts({ ...opts, categoryMode: e.target.value as 'merge' | 'replace' })}
            >
              <option value="merge">{t('admin.imports.categoryModes.merge')}</option>
              <option value="replace">{t('admin.imports.categoryModes.replace')}</option>
            </Select>
          )}
        </Field>
      </div>
      <div className="mt-4 flex items-center gap-1">
        <FilePicker
          busy={busy}
          onFile={(f) => upload(f)}
          label={t('admin.imports.chooseFile')}
          accept={PRODUCT_FILE_TYPES}
        />
        <HelpTip k="imports.products_file" />
      </div>
      {preview && (
        <div className="mt-4 space-y-3 rounded-md border border-line p-4">
          <div className="flex flex-wrap gap-2">
            <Badge tone="neutral">{t(`admin.imports.formats.${preview.format}`)}</Badge>
            <Badge tone="moss">{t('admin.imports.toCreate', { count: preview.summary.create })}</Badge>
            <Badge tone="pine">{t('admin.imports.toUpdate', { count: preview.summary.update })}</Badge>
            {preview.summary.skipped > 0 && (
              <Badge tone="amber">{t('admin.imports.skipped', { count: preview.summary.skipped })}</Badge>
            )}
            <Badge tone="neutral">{t('admin.imports.variants', { count: preview.summary.variants })}</Badge>
            {preview.summary.images > 0 && (
              <Badge tone="neutral">{t('admin.imports.images', { count: preview.summary.images })}</Badge>
            )}
            {preview.summary.categoriesToCreate > 0 && (
              <Badge tone="amber">
                {t('admin.imports.categoriesToCreate', { count: preview.summary.categoriesToCreate })}
              </Badge>
            )}
          </div>
          {preview.unknownHeaders.length > 0 && (
            <p className="text-sm text-ink-3">
              {t('admin.imports.unknownHeaders', { list: preview.unknownHeaders.join(', ') })}
            </p>
          )}
          {preview.summary.missingSku > 0 && lastFile && (
            <div className="flex flex-wrap items-center gap-2 rounded-sm border border-line bg-surface-2 p-3 text-sm">
              <p className="min-w-0 flex-1">
                {t('admin.imports.skuMissing', { count: preview.summary.missingSku })}
              </p>
              <span className="flex items-center gap-1">
                <Button size="sm" loading={busy} onClick={() => void upload(lastFile, true, columnMap)}>
                  {t('admin.imports.skuGenerate')}
                </Button>
                <HelpTip k="imports.sku_generate" />
              </span>
            </div>
          )}
          {preview.summary.generatedSku > 0 && (
            <p className="text-sm text-ink-2">
              {t('admin.imports.skuGenerated', { count: preview.summary.generatedSku })}
            </p>
          )}
          <ColumnMapping
            columns={preview.columns}
            fields={preview.fields}
            value={columnMap}
            onChange={setColumnMap}
            busy={busy}
            onRemap={() => lastFile && void upload(lastFile, preview.summary.generatedSku > 0, columnMap)}
          />
          <Issues issues={preview.issues} fileName="urun-ice-aktarma" />
          {preview.sample.some((s) => s.warnings.length) && (
            <ul className="space-y-0.5 text-sm">
              {preview.sample
                .filter((s) => s.warnings.length)
                .map((s) => (
                  <li key={s.key}>
                    <strong>{s.title ?? s.key}</strong>:{' '}
                    {s.warnings
                      .map((w) => {
                        const [kind, val] = w.split(':');
                        return kind === 'health_claim'
                          ? t(`healthClaims.${val}`)
                          : `${t(`errors.${kind}`)} (${val})`;
                      })
                      .join(', ')}
                  </li>
                ))}
            </ul>
          )}
          <p className="text-sm text-ink-2">{t('admin.imports.productsDraftNote')}</p>
          <span className="flex items-center gap-1">
            <Button
              variant="primary"
              loading={apply.isPending}
              disabled={preview.summary.create + preview.summary.update === 0}
              onClick={() => apply.mutate(preview.jobId)}
            >
              {t('admin.imports.apply')}
            </Button>
            <HelpTip k="imports.products_apply" />
          </span>
        </div>
      )}
      {job.data && (
        <ImportJobResult
          job={job.data}
          retrying={retry.isPending}
          onRetry={() => retry.mutate(job.data!.id)}
        />
      )}
      {can('catalog.product.read') && <ImportedMediaPanel />}
    </Section>
  );
}

const PRODUCT_FILE_TYPES =
  '.csv,.xlsx,.json,.xml,text/csv,application/json,application/xml,text/xml,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Elle sütun eşleştirme (F5B-10): dosyadaki her sütun ve aktarılacağı alan. Tanınmayan sütun bir alana bağlanır
 * veya yok sayılır; "yeniden önizle" aynı dosyayı eşleştirmeyle gönderir. Uygulanınca sunucu eşleştirmeyi kaydeder.
 */
function ColumnMapping({
  columns,
  fields,
  value,
  onChange,
  onRemap,
  busy,
}: {
  columns: ProductImportPreview['columns'];
  fields: ProductImportPreview['fields'];
  value: Record<string, string>;
  onChange: (v: Record<string, string>) => void;
  onRemap: () => void;
  busy: boolean;
}) {
  const t = useT();
  const unmatched = columns.filter((c) => c.source === 'unmatched').length;
  const tone: Record<string, 'moss' | 'pine' | 'amber' | 'neutral' | 'slate'> = {
    auto: 'neutral',
    saved: 'pine',
    manual: 'pine',
    attribute: 'neutral',
    ignored: 'slate',
    unmatched: 'amber',
  };
  return (
    <section aria-labelledby="column-mapping" className="rounded-sm border border-line p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 id="column-mapping" className="text-sm font-medium">
          {t('admin.imports.columnMapping')}
        </h3>
        <HelpTip k="imports.column_map" />
        {unmatched > 0 && (
          <Badge tone="amber">{t('admin.imports.unmatchedCount', { count: unmatched })}</Badge>
        )}
      </div>
      <p className="mt-1 text-sm text-ink-3">{t('admin.imports.columnMappingLead')}</p>
      <div className="mt-2 max-h-80 overflow-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">{t('admin.imports.columnMapping')}</caption>
          <thead className="text-ink-2">
            <tr>
              <th className="py-1 text-start font-medium">{t('admin.imports.fileColumn')}</th>
              <th className="py-1 text-start font-medium">{t('admin.imports.targetField')}</th>
              <th className="py-1 text-start font-medium">{t('admin.imports.columnSource')}</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((c) => {
              const current = value[c.header] ?? (c.source === 'ignored' ? 'ignore' : (c.field ?? ''));
              return (
                <tr key={c.header} className="border-t border-line">
                  <td className="py-1 pe-3">{c.header}</td>
                  <td className="py-1 pe-3">
                    {c.source === 'attribute' ? (
                      <span className="text-ink-3">{t('admin.imports.attributeColumn')}</span>
                    ) : (
                      <Select
                        aria-label={`${c.header} — ${t('admin.imports.targetField')}`}
                        value={current}
                        onChange={(e) => {
                          const next = { ...value };
                          if (e.target.value) next[c.header] = e.target.value;
                          else delete next[c.header];
                          onChange(next);
                        }}
                      >
                        <option value="">{t('admin.imports.noField')}</option>
                        <option value="ignore">{t('admin.imports.ignoreColumn')}</option>
                        {fields.map((f) => (
                          <option key={f} value={f}>
                            {t(`admin.imports.fields.${f}`)}
                          </option>
                        ))}
                      </Select>
                    )}
                  </td>
                  <td className="py-1">
                    <Badge tone={tone[c.source]}>{t(`admin.imports.columnSources.${c.source}`)}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Button
        className="mt-2"
        size="sm"
        loading={busy}
        disabled={!Object.keys(value).length}
        onClick={onRemap}
      >
        {t('admin.imports.remap')}
      </Button>
    </section>
  );
}

/** Uygulanan içe aktarmanın sonucu; büyük dosyada ilerleme, hata varsa "Hatalıları yeniden dene". */
function ImportJobResult({
  job,
  retrying,
  onRetry,
}: {
  job: ProductImportStatus;
  retrying: boolean;
  onRetry: () => void;
}) {
  const t = useT();
  const running = (job.status === 'queued' || job.status === 'running') && !job.canRetry;
  return (
    <div className="mt-4 rounded-md border border-line p-4 text-sm" role="status">
      {running ? (
        <>
          <p className="font-medium">
            {t('admin.imports.importQueued', { processed: job.processed, total: job.total })}
          </p>
          <progress
            className="mt-2 h-2 w-full accent-[var(--zt-pine)]"
            max={Math.max(job.total, 1)}
            value={job.processed}
            aria-label={t('admin.imports.importProgress')}
          />
          <p className="mt-1 text-ink-3">{t('admin.imports.importQueuedLead')}</p>
        </>
      ) : (
        <>
          <p className="font-medium">
            {t('admin.imports.productResult', {
              created: job.summary.created,
              updated: job.summary.updated,
              failed: job.summary.failed,
            })}
          </p>
          {job.summary.stockSkippedLot > 0 && (
            <p className="mt-1 text-ink-2">
              {t('admin.imports.stockSkippedLot', { count: job.summary.stockSkippedLot })}
            </p>
          )}
          {job.failed.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-rose">
              {job.failed.map((r) => (
                <li key={r.key}>
                  {r.key}: {t(`errors.${r.error}`)}
                </li>
              ))}
            </ul>
          )}
          {job.canRetry && (
            <span className="mt-2 inline-flex items-center gap-1">
              <Button size="sm" loading={retrying} onClick={onRetry}>
                {t('admin.imports.retryFailed')}
              </Button>
              <HelpTip k="imports.retry" />
            </span>
          )}
        </>
      )}
    </div>
  );
}

/**
 * İçe aktarılan görsellerin indirme durumu (F3B-11): bekleyen ve indirilemeyen sayıları, nedenleri ve
 * toplu "Yeniden dene". İndirme sürerken 10 sn'de bir yenilenir.
 */
function ImportedMediaPanel() {
  const t = useT();
  const { can } = usePermissions();
  const q = useQuery({
    queryKey: ['imported-media-status'],
    queryFn: () => api.get<ImportedMediaStatus>('/v1/admin/products/media/status'),
    refetchInterval: (query) => ((query.state.data?.pending ?? 0) > 0 ? 10_000 : false),
  });
  const retry = useApiMutation(() => api.post<{ queued: number }>('/v1/admin/products/media/retry-failed'), {
    invalidate: [['imported-media-status'], ['product']],
    success: (r) => t('admin.imports.mediaRetryQueued', { count: r.queued }),
  });
  const s = q.data;
  if (!s || (s.pending === 0 && s.failed === 0)) return null;
  const reasons = Object.entries(s.reasons)
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => `${t(`admin.products.mediaFailure.${k}`)} (${n})`)
    .join(', ');
  return (
    <div className="mt-4 space-y-2 rounded-md border border-line p-4 text-sm">
      <p className="font-medium">{t('admin.imports.mediaStatusTitle')}</p>
      {s.pending > 0 && <p className="text-ink-2">{t('admin.imports.mediaPending', { count: s.pending })}</p>}
      {s.failed > 0 && (
        <>
          <p className="text-rose">{t('admin.imports.mediaFailed', { count: s.failed })}</p>
          <p className="text-ink-2">{t('admin.imports.mediaReasons', { list: reasons })}</p>
          {can('catalog.product.write') && (
            <span className="flex items-center gap-1">
              <Button size="sm" loading={retry.isPending} onClick={() => retry.mutate(undefined)}>
                {t('admin.imports.mediaRetryAll')}
              </Button>
              <HelpTip k="imports.media_retry_all" />
            </span>
          )}
        </>
      )}
    </div>
  );
}

function DealerImport() {
  const t = useT();
  const { can } = usePermissions();
  const groups = useGroups();
  const dealerGroups = (groups.data ?? []).filter((g) => g.kind === 'dealer');
  const [opts, setOpts] = useState({
    defaultGroupId: '',
    defaultStatus: 'approved',
    useCompanyEmailForLogin: true,
  });
  const [sendLinks, setSendLinks] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<DealerImportPreview | null>(null);
  const [result, setResult] = useState<DealerImportResult | null>(null);
  const apply = useApiMutation(
    (id: string) =>
      api.post<DealerImportResult>(
        `/v1/admin/imports/dealers/${id}/apply`,
        { sendAccessLinks: sendLinks },
        { idempotencyKey: newIdempotencyKey() },
      ),
    {
      invalidate: [['companies'], ['customers']],
      onSuccess: (r) => {
        setResult(r);
        setPreview(null);
        toast.success(t('admin.imports.applied'));
      },
    },
  );
  const upload = async (file: File) => {
    setBusy(true);
    setResult(null);
    try {
      const qs = new URLSearchParams();
      if (opts.defaultGroupId) qs.set('defaultGroupId', opts.defaultGroupId);
      qs.set('defaultStatus', opts.defaultStatus);
      qs.set('useCompanyEmailForLogin', String(opts.useCompanyEmailForLogin));
      const fd = new FormData();
      fd.append('file', file);
      setPreview(
        await api.upload<DealerImportPreview>(`/v1/admin/imports/dealers/preview?${qs.toString()}`, fd),
      );
    } catch (e) {
      toast.error(e instanceof ApiError ? e.title : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section
      title={t('admin.imports.dealers')}
      description={t('admin.imports.dealersLead')}
      action={
        <div className="flex flex-wrap gap-2">
          <DownloadButton
            path="/v1/admin/imports/dealers/template?format=xlsx"
            name="bayi-sablonu.xlsx"
            label={t('admin.imports.template')}
          />
          {can('b2b.dealer.read') && (
            <DownloadButton
              path="/v1/admin/exports/dealers?format=xlsx"
              name="bayiler.xlsx"
              label={t('admin.imports.exportDealers')}
            />
          )}
        </div>
      }
    >
      {dealerGroups.length === 0 && (
        <p className="mb-3 text-sm text-amber">{t('admin.imports.noDealerGroups')}</p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('admin.imports.defaultGroup')} help={<HelpTip k="dealers.group" />}>
          {(p) => (
            <Select
              {...p}
              value={opts.defaultGroupId}
              onChange={(e) => setOpts({ ...opts, defaultGroupId: e.target.value })}
            >
              <option value="">—</option>
              {dealerGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.imports.defaultStatus')} help={<HelpTip k="imports.default_status" />}>
          {(p) => (
            <Select
              {...p}
              value={opts.defaultStatus}
              onChange={(e) => setOpts({ ...opts, defaultStatus: e.target.value })}
            >
              <option value="approved">{t('admin.dealers.statuses.approved')}</option>
              <option value="applied">{t('admin.dealers.statuses.applied')}</option>
            </Select>
          )}
        </Field>
      </div>
      <div className="mt-3 space-y-2">
        <div className="flex items-center gap-3">
          <Switch
            checked={opts.useCompanyEmailForLogin}
            onCheckedChange={(v) => setOpts({ ...opts, useCompanyEmailForLogin: v })}
            label={t('admin.imports.useCompanyEmail')}
          />
          <span className="text-sm">{t('admin.imports.useCompanyEmail')}</span>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={sendLinks} onCheckedChange={setSendLinks} label={t('admin.imports.sendLinks')} />
          <span className="text-sm">{t('admin.imports.sendLinks')}</span>
          <HelpTip k="imports.send_links" />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-1">
        <FilePicker busy={busy} onFile={upload} label={t('admin.imports.chooseFile')} />
        <HelpTip k="imports.dealers_file" />
      </div>
      {preview && (
        <div className="mt-4 space-y-3 rounded-md border border-line p-4">
          <div className="flex flex-wrap gap-2">
            <Badge tone="moss">{t('admin.imports.toCreate', { count: preview.summary.create })}</Badge>
            <Badge tone="pine">{t('admin.imports.toUpdate', { count: preview.summary.update })}</Badge>
            <Badge tone="neutral">{t('admin.imports.unchanged', { count: preview.summary.unchanged })}</Badge>
            {preview.summary.error > 0 && (
              <Badge tone="amber">{t('admin.imports.errorRows', { count: preview.summary.error })}</Badge>
            )}
            <Badge tone="neutral">{t('admin.imports.members', { count: preview.summary.members })}</Badge>
          </div>
          {preview.unknownHeaders.length > 0 && (
            <p className="text-sm text-ink-3">
              {t('admin.imports.unknownHeaders', { list: preview.unknownHeaders.join(', ') })}
            </p>
          )}
          <Issues issues={preview.issues} fileName="bayi-ice-aktarma" />
          {preview.sample.length > 0 && (
            <div className="max-h-72 overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-ink-2">
                  <tr>
                    <th className="py-1 text-start font-medium">
                      {t('admin.imports.row', { row: '' }).trim()}
                    </th>
                    <th className="py-1 text-start font-medium">{t('admin.dealers.legalName')}</th>
                    <th className="py-1 text-start font-medium">{t('admin.dealers.taxNumber')}</th>
                    <th className="py-1 text-start font-medium">{t('admin.imports.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.map((s) => (
                    <tr key={s.row} className="border-t border-line">
                      <td className="py-1 tabular">{s.row}</td>
                      <td className="py-1">{s.legalName}</td>
                      <td className="py-1 tabular">{s.taxNumber ?? '—'}</td>
                      <td className="py-1">{t(`admin.imports.actions.${s.action}`)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <span className="flex items-center gap-1">
            <Button
              variant="primary"
              loading={apply.isPending}
              disabled={preview.summary.create + preview.summary.update + preview.summary.unchanged === 0}
              onClick={() => apply.mutate(preview.jobId)}
            >
              {t('admin.imports.apply')}
            </Button>
            <HelpTip k="imports.dealers_apply" />
          </span>
        </div>
      )}
      {result && (
        <div className="mt-4 space-y-1 rounded-md border border-line p-4 text-sm" role="status">
          <p className="font-medium">
            {t('admin.imports.dealerResult', {
              created: result.summary.created,
              updated: result.summary.updated,
              members: result.summary.membersAdded,
            })}
          </p>
          {result.summary.linksEmailed > 0 && (
            <p>{t('admin.imports.linksEmailed', { count: result.summary.linksEmailed })}</p>
          )}
          {result.summary.needActivation > 0 && (
            <p className="text-amber">
              {t('admin.imports.needActivation', { count: result.summary.needActivation })}
            </p>
          )}
        </div>
      )}
    </Section>
  );
}
